/**
 * Logger Service Provider for EOS Framework
 * 
 * Usage:
 *   const logger = this.core.make('oxzion/logger');
 *   logger.debug/info/warn/error/fatal(message, context);
 *   logger.log(level, message, context);
 * 
 * Features:
 * - WebSocket transmission with console fallback
 * - Safe JSON serialization
 * - Set includeSource: false in context to disable tracking
 */

import { ServiceProvider } from '@osjs/common';
import callsites from 'callsites';

export class LoggerServiceProvider extends ServiceProvider {

    constructor(core, options = {}) {
        super(core, options || {});
        this.core = core;
    }

    providers() {
        return [
            'oxzion/logger'
        ];
    }

    init() {
        this.core.instance('oxzion/logger', () => ({
            log: (type, message, context = {}) => this.log(type, message, context),
            debug: (message, context = {}) => this.log('debug', message, context),
            info: (message, context = {}) => this.log('info', message, context),
            warn: (message, context = {}) => this.log('warn', message, context),
            error: (message, context = {}) => this.log('error', message, context),
            fatal: (message, context = {}) => this.log('fatal', message, context)
        }));
    }

    // Safe JSON stringifier that handles circular references
    safeStringify(obj, space) {
        const seen = new WeakSet();
        return JSON.stringify(obj, (key, value) => {
            // Handle circular references
            if (typeof value === "object" && value !== null) {
                if (seen.has(value)) {
                    return "[Circular Reference]";
                }
                seen.add(value);
            }
            
            // Skip React internals and other potentially problematic properties
            if (typeof value === "object" && value !== null) {
                if (key === '_reactInternals' || 
                    key === '_reactInternalFiber' ||
                    key === 'stateNode' ||
                    key === '_owner' ||
                    key === '_source') {
                    return "[React Internal]";
                }
            }
            
            return value;
        }, space);
    }

    log(type, message, context = {}) {
        if (this.core.ws && this.core.ws.connected) {
            let enrichedContext = context;
            
            // Use callsites to get caller information efficiently
            // Skip if explicitly disabled or if source is already provided
            if (context.includeSource !== false && !context.source) {
                try {
                    const sites = callsites();
                    // sites[0] is this function, sites[1] is the convenience method or direct caller
                    // sites[2] is typically the actual caller we want
                    const caller = sites[2] || sites[1];
                    
                    if (caller) {
                        const fileName = caller.getFileName();
                        const lineNumber = caller.getLineNumber();
                        
                        if (fileName && lineNumber) {
                            // Extract just the filename or last 2 path segments for context
                            const pathParts = fileName.split('/');
                            const shortPath = pathParts.length > 2 
                                ? pathParts.slice(-2).join('/')
                                : pathParts[pathParts.length - 1] || fileName;
                            
                            enrichedContext = {
                                source: `${shortPath}:${lineNumber}`,
                                ...context
                            };
                        }
                    }
                } catch (e) {
                    // Silently fail if callsites doesn't work (shouldn't happen)
                    console.warn('Failed to capture caller info:', e);
                }
            }
            
            try {
                this.core.ws.send(this.safeStringify({
                    name: 'logger',
                    params: [{
                        level: type,
                        message,
                        context: enrichedContext
                    }]
                }));
            } catch (error) {
                // Fallback to console logging if WebSocket serialization fails
                console.log(`[${type.toUpperCase()}] ${message}`, enrichedContext);
                console.warn('Logger WebSocket send failed:', error);
            }
        } else {
            // Fallback to console when WebSocket is not available
            console.log(`[${type.toUpperCase()}] ${message}`, context);
        }
    }

}